import { useCallback, useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import type { TelemetryFile } from '@/types';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import TelemetryDetailsModal from './TelemetryDetailsModal';
import FileCard from './FileCard';
import { FilesListLoading, FilesListError } from './FilesListStates';
import {
  UploadStatusIcon,
} from './uploadStatus';
import {
  getUploadStatusText,
  type UploadStatusLabels,
} from './uploadStatusText';
import { handleApiError, type ApiError } from '@/utils/errorHandler';
import { formatFileSize } from '@/utils/fileUtils';
import { openPresignedDownload } from '@/utils/download';
import { formatUploadDuration } from '@/utils/duration';
import { formatDateTimeOptional } from '@/utils/dateFormat';

interface TelemetryFilesListProps {
  sessionId: string;
  onFilesChange?: (telemetryFiles: TelemetryFile[]) => void;
  canManage?: boolean;
  refreshKey?: number;
}

function formatDuration(
  seconds: number | null | undefined,
  unknownDuration: string
): string {
  if (seconds == null) return unknownDuration;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return formatUploadDuration(seconds);
}

function formatCount(value: number | null | undefined, unknown: string): string {
  if (value == null) return unknown;
  return value.toLocaleString();
}

function formatLabeledCount(
  value: number | null | undefined,
  unknown: string,
  formatter: (count: number) => string
): string {
  return value == null ? unknown : formatter(value);
}

export default function TelemetryFilesList({
  sessionId,
  onFilesChange,
  canManage = true,
  refreshKey = 0,
}: TelemetryFilesListProps) {
  const { t } = useTranslation('pages');
  const [telemetryFiles, setTelemetryFiles] = useState<TelemetryFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTelemetryFile, setSelectedTelemetryFile] =
    useState<TelemetryFile | null>(null);
  const [telemetryFileToDelete, setTelemetryFileToDelete] =
    useState<TelemetryFile | null>(null);
  const [isDeletingTelemetryData, setIsDeletingTelemetryData] = useState(false);

  const fetchTelemetryFiles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getTelemetryFiles(sessionId);
      const files = data.telemetryFiles || [];
      setTelemetryFiles(files);
      onFilesChange?.(files);
      setError('');
    } catch (err) {
      onFilesChange?.([]);
      setError(t('sessions.detail.telemetry.files.loadError'));
      console.error('Error fetching telemetry files:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId, t, onFilesChange]);

  useEffect(() => {
    fetchTelemetryFiles();
  }, [fetchTelemetryFiles, refreshKey]);

  const handleConfirmDelete = async () => {
    if (!telemetryFileToDelete) return;

    try {
      setIsDeletingTelemetryData(true);
      await apiClient.deleteTelemetryData(sessionId);
      setTelemetryFiles([]);
      onFilesChange?.([]);
      setSelectedTelemetryFile(null);
      setTelemetryFileToDelete(null);
      setError('');
    } catch (error) {
      console.error('Error deleting telemetry data:', error);
      handleApiError(
        error as ApiError,
        t('sessions.detail.telemetry.files.deleteError')
      );
    } finally {
      setIsDeletingTelemetryData(false);
    }
  };

  const handleDownload = async (telemetryFile: TelemetryFile) => {
    try {
      const downloadData = await apiClient.getTelemetryFileDownloadUrl(
        telemetryFile.id,
      );
      // Content-Disposition on the presigned URL names the file.
      openPresignedDownload(downloadData.downloadUrl);
    } catch (error) {
      console.error("Error downloading telemetry file:", error);
      handleApiError(
        error as ApiError,
        t("sessions.detail.telemetry.files.downloadError"),
      );
    }
  };

  if (loading) {
    return (
      <FilesListLoading
        label={t('sessions.detail.telemetry.files.loading')}
      />
    );
  }

  if (error) {
    return <FilesListError message={error} />;
  }

  const statusLabels: UploadStatusLabels = {
    uploaded: t('sessions.detail.telemetry.files.status.uploaded'),
    uploading: t('sessions.detail.telemetry.files.status.uploading'),
    failed: t('sessions.detail.telemetry.files.status.failed'),
    pending: t('sessions.detail.telemetry.files.status.pending'),
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-secondary-500">
            {t('sessions.detail.telemetry.files.count', {
              count: telemetryFiles.length,
            })}
          </span>
        </div>

        {telemetryFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-secondary-500">
            <FileText className="w-12 h-12 mb-2" />
            <p>{t('sessions.detail.telemetry.files.empty')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {telemetryFiles.map((telemetryFile) => (
              <FileCard
                key={telemetryFile.id}
                icon={FileText}
                filename={telemetryFile.filename}
                statusIcon={
                  <UploadStatusIcon status={telemetryFile.uploadStatus} />
                }
                statusText={getUploadStatusText(
                  telemetryFile.uploadStatus,
                  statusLabels
                )}
                fileSizeLabel={formatFileSize(telemetryFile.fileSize, {
                  unknownLabel: t(
                    'sessions.detail.telemetry.files.unknownSize'
                  ),
                })}
                mobileDetails={
                  <>
                    <span>
                      {formatDateTimeOptional(
                        telemetryFile.uploadedAt,
                        t('sessions.detail.telemetry.files.unknown')
                      )}
                    </span>
                    <span>
                      {t('sessions.detail.telemetry.files.summary', {
                        channels: formatCount(
                          telemetryFile.channelCount,
                          t('sessions.detail.telemetry.files.unknown')
                        ),
                        points: formatCount(
                          telemetryFile.dataPointCount,
                          t('sessions.detail.telemetry.files.unknown')
                        ),
                        duration: formatDuration(
                          telemetryFile.duration,
                          t('sessions.detail.telemetry.files.unknownDuration')
                        ),
                      })}
                    </span>
                  </>
                }
                desktopMeta={
                  <>
                    <span>
                      {formatLabeledCount(
                        telemetryFile.channelCount,
                        t('sessions.detail.telemetry.files.unknown'),
                        (count) =>
                          t('sessions.detail.telemetry.files.channels', {
                            count,
                            formattedCount: count.toLocaleString(),
                          })
                      )}
                    </span>
                    <span>
                      {formatLabeledCount(
                        telemetryFile.dataPointCount,
                        t('sessions.detail.telemetry.files.unknown'),
                        (count) =>
                          t('sessions.detail.telemetry.files.points', {
                            count,
                            formattedCount: count.toLocaleString(),
                          })
                      )}
                    </span>
                    <span>
                      {formatDuration(
                        telemetryFile.duration,
                        t('sessions.detail.telemetry.files.unknownDuration')
                      )}
                    </span>
                    {telemetryFile.uploadedAt && (
                      <span>
                        {formatDateTimeOptional(
                          telemetryFile.uploadedAt,
                          t('sessions.detail.telemetry.files.unknown')
                        )}
                      </span>
                    )}
                  </>
                }
                onDetails={() => setSelectedTelemetryFile(telemetryFile)}
                onDownload={
                  canManage && telemetryFile.uploadStatus === "completed"
                    ? () => handleDownload(telemetryFile)
                    : undefined
                }
                onDelete={
                  canManage
                    ? () => setTelemetryFileToDelete(telemetryFile)
                    : undefined
                }
                labels={{
                  details: t('sessions.detail.telemetry.files.viewDetails'),
                  detailsAriaLabel: t(
                    'sessions.detail.telemetry.files.viewDetailsFor',
                    { filename: telemetryFile.filename }
                  ),
                  download: t('sessions.detail.telemetry.files.download'),
                  downloadAriaLabel: t(
                    'sessions.detail.telemetry.files.downloadFor',
                    { filename: telemetryFile.filename }
                  ),
                  delete: t('sessions.detail.telemetry.files.delete'),
                  deleteAriaLabel: t(
                    'sessions.detail.telemetry.files.deleteFor',
                    { filename: telemetryFile.filename }
                  ),
                }}
              />
            ))}
          </div>
        )}
      </div>

      <TelemetryDetailsModal
        isOpen={!!selectedTelemetryFile}
        onClose={() => setSelectedTelemetryFile(null)}
        telemetryFile={selectedTelemetryFile}
      />

      <ConfirmationModal
        isOpen={!!telemetryFileToDelete}
        onClose={() => setTelemetryFileToDelete(null)}
        onConfirm={handleConfirmDelete}
        title={t('sessions.detail.telemetry.files.deleteTitle')}
        message={
          telemetryFileToDelete
            ? t('sessions.detail.telemetry.files.deleteMessage', {
                filename: telemetryFileToDelete.filename,
              })
            : ''
        }
        confirmButtonText={t('sessions.detail.telemetry.files.deleteButton')}
        confirmButtonColor="red"
        isLoading={isDeletingTelemetryData}
      />
    </>
  );
}
